import React, { useEffect } from "react";
import { gsap } from "gsap";

import "../../scss/global.scss";
import "./home.scss";
import Gallery from "../../components/gallery/gallery";

import { ScrollTrigger } from "gsap/ScrollTrigger";
import useIsMobile from "../../hooks/useIsMobile";
import Icon from "../../animations/icon";

gsap.registerPlugin(ScrollTrigger);

const Home: React.FC = () => {
    const isMobile = useIsMobile();
    const images = isMobile
        ? [
              "/images/gallery/mobile/1.jpg",
              "/images/gallery/mobile/2.jpg",
              "/images/gallery/mobile/3.jpg",
              "/images/gallery/mobile/4.jpg",
              "/images/gallery/mobile/5.jpg",
              "/images/gallery/mobile/6.jpg",
              "/images/gallery/mobile/7.jpg",
              "/images/gallery/mobile/8.jpg",
          ]
        : [
              "/images/gallery/1.jpg",
              "/images/gallery/2.jpg",
              "/images/gallery/3.jpg",
              "/images/gallery/4.jpg",
          ];
    useEffect(() => {
        const timeline = gsap.timeline();

        timeline
            .fromTo(
                ".copy-one",
                { opacity: 0 },
                { opacity: 1, duration: 0.75, ease: "power2.in" }
            )
            .to(".copy-one", {
                opacity: 0,
                duration: 0.75,
                delay: 0.75,
                ease: "power2.out",
            })
            .fromTo(
                ".path",
                {
                    opacity: 0,
                },
                {
                    opacity: 1,
                    duration: 1,
                    stagger: 0.025,
                    ease: "power2.in",
                }
            )
            .fromTo(
                ".icon",
                {
                    y: -25,
                },
                {
                    y: -25,
                    ease: "sin.out",
                    duration: 1.5,
                    delay: 0.25,
                }
            )
            .fromTo(
                ".copy-two",
                {
                    opacity: 0,
                },
                {
                    opacity: 1,
                    duration: 1.25,
                    ease: "power2.in",
                },
                "<"
            );

        ScrollTrigger.create({
            trigger: ".home-one",
            start: "top top",
            end: "bottom 75%",
            onUpdate: (self) => {
                const opacity = 1 - self.progress;
                gsap.to(".copy-two-wrapper", { opacity: opacity });
            },
        });

        gsap.timeline({
            scrollTrigger: {
                trigger: ".copy-intro",
                start: `${!isMobile ? "top bottom" : "top 75%"}`,
                end: "top top",
                toggleActions: "play reverse play reverse",
            },
        }).fromTo(
            ".copy-intro",
            { opacity: 0 },
            { opacity: 1, duration: 1, ease: "power2.in" }
        );

        gsap.timeline({
                scrollTrigger: {
                    trigger: ".section-wrapper",
                    start: "top center",
                    end: "bottom center",
                    toggleActions: "play reverse play reverse",
                },
            })
            .to(".img-wrapper", { opacity: 1, ease: "power2.in" })
            .fromTo(
                ".copy-wrapper",
                { opacity: 0 },
                { opacity: 1, ease: "power2.in" }
            );

        gsap.timeline({
                scrollTrigger: {
                    trigger: ".vert-line-one-wrapper",
                    start: "top 75%",
                    end: "bottom 50%",
                    toggleActions: "play reverse play reverse",
                    scrub: true,
                },
            })
            .fromTo(
                ".vert-line-one",
                { height: 0 },
                { height: "100%", ease: "linear" }
            );

        gsap.timeline({
                scrollTrigger: {
                    trigger: ".vert-line-two-wrapper",
                    start: "top 75%",
                    end: "bottom 50%",
                    toggleActions: "play reverse play reverse",
                    scrub: true,
                },
            })
            .fromTo(
                ".vert-line-two",
                { height: 0 },
                { height: "100%", ease: "linear" }
            );

        gsap.timeline({
                scrollTrigger: {
                    trigger: ".content",
                    start: "top 75%",
                    end: "top top",
                    toggleActions: "play none none none",
                },
            })
            .fromTo(
                ".content",
                { opacity: 0 },
                { opacity: 1, ease: "power2.in", stagger: 0.25 }
            );

        return () => {
            timeline.kill();
            ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
        };
    }, [isMobile]);

    useEffect(() => {
        gsap.fromTo(
            "#gallery",
            { top: isMobile ? 50 : 150 },
            {
                top: isMobile ? -50 : -150,
                scrollTrigger: {
                    trigger: ".home-three",
                    start: "top bottom",
                    end: "top top",
                    scrub: true,
                },
            }
        );

        return () => {
            ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
        };
    }, [isMobile]);

    return (
        <>
            <a href="/dl/brochure.pdf" target="_blank" className="brochure">
                <h4>
                    Download
                    <br /> Brochure
                </h4>
            </a>
            <div
                className="home-one"
                style={{ backgroundImage: "url(/images/1.jpg)" }}
            >
                <div className="copy-one">
                    <img src="/images/text.svg" alt="logo" />
                </div>
                <div className="copy-two-wrapper">
                    <Icon />
                    <div className="copy-two">
                        <img src="/images/name-logo.svg" alt="logo" />
                    </div>
                </div>
            </div>
            <div className="home-two-wrapper">
                <div className="home-two contain">
                    <div className="intro-copy">
                        <h2 className="copy-intro">
                            Welcome to 7 Down Street, a hidden gem, nestled
                            between two of London’s most iconic parks and a
                            stone’s throw away from bustling Shepherd Market and
                            vibrant Curzon Street.
                        </h2>
                    </div>
                    <div className="vert-line-one-wrapper">
                        <div className="vert-line-one"></div>
                    </div>
                    <div className="line-one"></div>
                    <div className="section-wrapper">
                        <div className="left-wrapper">
                            <div className="copy-wrapper">
                                <div className="line-header"></div>
                                <h4>AVAILABILITY</h4>
                                <h1>7,308 sq ft</h1>
                                <p>
                                    Comprehensively
                                    <br />
                                    refurbished office space
                                </p>
                            </div>
                            <div className="copy-wrapper">
                                <div className="line-header"></div>
                                <h4>ACCOMMODATION</h4>
                                <h1>2 floors left</h1>
                                <p>CAT A / CAT A+</p>
                            </div>
                        </div>
                        <div
                            className="img-wrapper"
                            style={{ backgroundImage: "url(/images/2.jpg)" }}
                        ></div>
                        <div className="right-wrapper">
                            <div className="copy-wrapper">
                                <div className="line-header"></div>
                                <h4>FlOORS CIRCA</h4>
                                <h1>5,000 sq ft</h1>
                                <p>May be split</p>
                            </div>
                        </div>
                    </div>
                    <div className="vert-line-two-wrapper">
                        <div className="vert-line-two"></div>
                    </div>
                </div>
            </div>
            <div className="home-three-wrapper">
                <div className="home-three contain">
                    <div className="gallery-wrapper">
                        <Gallery images={images} />
                    </div>
                    <div className="content-wrapper">
                        <div className="content">
                            <h4>SUSTAINABILITY</h4>
                            <h1>
                                Energy
                                <br />
                                efficient
                            </h1>
                            <h3>Improvements throughout</h3>
                        </div>
                        <div className="content">
                            <h4>ENTRANCE EXPERIENCE</h4>
                            <h1>
                                Redesigned
                                <br />
                                reception
                            </h1>
                            <h3>High quality finishes</h3>
                        </div>
                        <div className="content">
                            <h4>End-of-journey</h4>
                            <h1>
                                Shower
                                <br />
                                facilities
                            </h1>
                            <h3>Plus bike racks and lockers</h3>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Home;
